import {useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import AWS from "aws-sdk";
import {UUIDv4} from "../../../support";
import {useAuth} from "../../../contexts/AuthContext";
import CFASNavbar from "../../../common/SACC/CareersFairContext/CFASNavbar";
import CFASFooter from "../../../common/SACC/CareersFairContext/CFASFooter";
import {previewEnabled, fetchPublicRoles} from "../../../features/roles/roles";

// New: getProfile() is also called, read-only, to show the applicant's details.

const isLocalhost = ["localhost", "127.0.0.1"].includes(window.location.hostname);
const roleIDFromUrl = () => new URLSearchParams(window.location.search).get("roleID");

// S3 settings
const CV_BUCKET = "cfas-user-cv-bucket";
const BUCKET_REGION = "eu-west-2";
const IDENTITY_POOL_ID = "eu-west-2:67102926-3c2f-4e94-b91a-8058ee31d8b8";

function initials(name) {
    if (!name) return "";
    return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}

const ApplySubmit = (props) => {
    const navigate = useNavigate();
    const {submitApplication, companyInfo, getRole, getProfile} = useAuth();

    const [roleData, setRoleData] = useState({});
    const [companyData, setCompanyData] = useState({});
    const [profile, setProfile] = useState(null);     // null = not loaded (or unavailable)
    const [loadError, setLoadError] = useState("");
    const [logoFailed, setLogoFailed] = useState(false);

    const [additionalText, setAdditionalText] = useState("");
    const [files, setFiles] = useState([]);
    const [dragging, setDragging] = useState(false);
    const [checked, setChecked] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [sendError, setSendError] = useState("");
    const fileInput = useRef(null);

    const roleLoaded = Boolean(roleData["position-name"]);
    const companyName = companyData["company-name"] || "the company";
    const needsFiles = Boolean(roleData["cover-letter"]);

    // ---------- Load role, company and profile ----------
    function handleAuthError(data, status) {
        if ((status === 401 || data?.data?.["reason"] === "Invalid Token") && !isLocalhost) {
            window.location.href = "/cfas/hub/sign-in?nav=/cfas/apply/submit&roleID=" + encodeURIComponent(roleIDFromUrl() || "");
            return true;
        }
        return false;
    }

    useEffect(() => {
        if (previewEnabled) {
            // Frontend preview: build the page from the example roles instead of the server.
            fetchPublicRoles().then((all) => {
                const role = all.find((r) => r.id === roleIDFromUrl()) || all[0];
                if (!role) {
                    setLoadError("No example roles to preview.");
                    return;
                }
                setRoleData({
                    "position-name": role.title,
                    "position-description": role.description || "",
                    "company-id": role.company?.id || role.company?.name,
                    "additional-question": "",
                    "cover-letter": false,
                });
                setCompanyData({
                    "company-name": role.company?.name || "Example Company",
                    "logo": role.company?.logoUrl || "",
                });
            }).catch((err) => setLoadError(err.message));
        } else {
            props.setShowCover(true);

            getRole(roleIDFromUrl()).then(async (roleRes) => {
                props.setShowCover(false);
                const data = await roleRes.json();
                if (roleRes.status === 200) {
                    setRoleData(data.data);
                    companyInfo(data.data["company-id"]).then(async (companyRes) => {
                        const cData = await companyRes.json();
                        if (companyRes.status === 200) setCompanyData(cData.data);
                    }).catch((err) => console.log(err));
                } else if (!handleAuthError(data, roleRes.status)) {
                    setLoadError(data?.data?.["reason"] || "We couldn't load this role.");
                }
            }).catch((err) => {
                console.log(err);
                props.setShowCover(false);
                setLoadError("We couldn't reach the server. Check your connection and try again.");
            });
        }

        // Read-only: shows who is applying. If it fails, the page still works without the summary.
        if (getProfile) {
            getProfile().then(async (res) => {
                const data = await res.json();
                if (res.status === 200 && data.message === "Success") setProfile(data.data || {});
            }).catch((err) => console.log(err));
        }
    }, []);

    // Files
    function addFiles(fileList) {
        const incoming = Array.from(fileList || []);
        if (!incoming.length) return;
        setFiles((prev) => {
            const names = new Set(prev.map((f) => f.name));
            return [...prev, ...incoming.filter((f) => !names.has(f.name))];
        });
    }
    function removeFile(name) {
        setFiles((prev) => prev.filter((f) => f.name !== name));
    }

    // ---------- Send ----------
    function submit() {
        if (submitting || !canSend) return;
        setSubmitting(true);
        setSendError("");

        props.setShowCover(true);

        if (files.length > 0) {
            AWS.config.update({
                region: BUCKET_REGION,
                credentials: new AWS.CognitoIdentityCredentials({IdentityPoolId: IDENTITY_POOL_ID}),
            });
            const uploads = files.map((f) => {
                const key = encodeURI(UUIDv4() + f.name.replace(/[^A-Za-z0-9.]/g, ""));
                const upload = new AWS.S3.ManagedUpload({params: {Bucket: CV_BUCKET, Key: key, Body: f}});
                const link = "https://" + CV_BUCKET + ".s3.eu-west-2.amazonaws.com/" + key;
                return upload.promise().then(() => link);
            });
            Promise.all(uploads).then(
                (links) => saveToDB(links.length === 1 ? links[0] : JSON.stringify(links)),
                (err) => fail("There was an error uploading your files: " + err.message),
            );
        } else {
            saveToDB("X");
        }
    }

    function saveToDB(link) {
        submitApplication(roleIDFromUrl(), additionalText, link).then(async (res) => {
            const data = await res.json();
            if (res.status === 200) {
                props.setShowCover(false);
                navigate("/cfas/track/home");
            } else {
                fail(data?.data?.["reason"] || "Your application didn't go through. Please try again.");
            }
        }).catch((err) => {
            console.log(err);
            fail("We couldn't reach the server. Check your connection and try again.");
        });
    }

    function fail(message) {
        setSubmitting(false);
        props.setShowCover(false);
        setSendError(message);
    }

    // What's still needed before Send unlocks, shown under the button.
    const missing = [];
    if (needsFiles && files.length === 0) missing.push("add at least one file");
    if (!checked) missing.push("tick the sharing agreement");
    const canSend = roleLoaded && missing.length === 0;

    // Profile summary
    const p = profile || {};
    const fullName = p["fullName"] || "";
    const university = (p["university"] || "").replace("Other//", "");
    const study = [p["courseName"], p["level"], p["year"]].filter((v) => v && v !== "-").join(", ");
    const cvLink = p["cvLink"] || "";

    return (
        <div className="ms">
            <style>{CSS}</style>
            <CFASNavbar userName={fullName}/>

            <main className="ms-main">
                <div className="ms-in">
                    <div className="ms-narrow ms-stack">

                        {/* Breadcrumb + title */}
                        <nav className="ms-crumbs" aria-label="Breadcrumb">
                            <button className="ms-link" onClick={() => navigate("/cfas/roles")}>Find roles</button>
                            <span aria-hidden="true">/</span>
                            {roleLoaded
                                ? <button className="ms-link" onClick={() => navigate("/cfas/roles/" + encodeURIComponent(roleIDFromUrl() || ""))}>{companyData["company-name"] || "Role"}</button>
                                : <span>Role</span>}
                            <span aria-hidden="true">/</span>
                            <span>Apply</span>
                        </nav>
                        <header className="ms-title">
                            <h1>Check and send</h1>
                            <p className="ms-muted">This goes to {companyName} with your profile and CV.</p>
                        </header>

                        {loadError && (
                            <div className="ms-card ms-empty">
                                <h3>We couldn't load this role</h3>
                                <p className="ms-muted">{loadError}</p>
                                <button className="ms-btn s" onClick={() => navigate("/cfas/roles")}>Back to Find roles</button>
                            </div>
                        )}

                        {!loadError && <>
                            {/* Role summary */}
                            <section className="ms-card">
                                <div className="ms-role-head">
                                    <div className="ms-logo">
                                        {companyData["logo"] && !logoFailed
                                            ? <img src={companyData["logo"]} alt={companyName + " logo"} onError={() => setLogoFailed(true)}/>
                                            : <span>{initials(companyData["company-name"])}</span>}
                                    </div>
                                    <div className="ms-grow">
                                        <span className="ms-lbl">{companyData["company-name"] || " "}</span>
                                        <h2>{roleLoaded ? roleData["position-name"] : "Loading role…"}</h2>
                                    </div>
                                </div>
                                {roleData["position-description"] && (
                                    <details className="ms-details">
                                        <summary>About the role</summary>
                                        <div className="ms-desc" dangerouslySetInnerHTML={{__html: roleData["position-description"].replace("//", "")}}/>
                                    </details>
                                )}
                            </section>

                            {/* Your profile */}
                            <section className="ms-card">
                                <div className="ms-card-head">
                                    <h3>Your profile</h3>
                                    <button className="ms-btn s sm" onClick={() => navigate("/cfas/hub/register")}>Edit</button>
                                </div>
                                {profile && (fullName || university || study) ? (
                                    <dl className="ms-dl">
                                        {fullName && <><dt>Name</dt><dd>{fullName}</dd></>}
                                        {university && <><dt>University</dt><dd>{university}</dd></>}
                                        {study && <><dt>Study</dt><dd>{study}</dd></>}
                                    </dl>
                                ) : (
                                    <p className="ms-muted ms-small">Your CFAS profile is sent with this application. Check it's up to date before you send.</p>
                                )}
                            </section>

                            {/* Your CV */}
                            <section className="ms-card">
                                <h3>Your CV</h3>
                                {cvLink ? (
                                    <div className="ms-cv ok">
                                        <b>CV on file</b>
                                        <span className="ms-small">The CV on your profile is sent with this application.</span>
                                        <a className="ms-link" href={cvLink} target="_blank" rel="noopener noreferrer">View your CV</a>
                                    </div>
                                ) : (
                                    <div className="ms-cv">
                                        <b>From your profile</b>
                                        <span className="ms-small">The CV saved on your CFAS profile is sent with this application. To change it, update your profile.</span>
                                        <button className="ms-link" onClick={() => navigate("/cfas/hub/register")}>Update CV</button>
                                    </div>
                                )}
                            </section>

                            {/* Question */}
                            <section className="ms-card">
                                <label className="ms-q" htmlFor="answer">
                                    <h3>{roleData["additional-question"] || `Anything else ${companyName} should know?`}</h3>
                                    {!roleData["additional-question"] && <span className="ms-muted ms-small">Optional</span>}
                                </label>
                                <textarea
                                    id="answer"
                                    rows={5}
                                    value={additionalText}
                                    onChange={(e) => setAdditionalText(e.target.value)}
                                    placeholder="Type your answer here"
                                    disabled={submitting}
                                />
                            </section>

                            {/* Extra files, only when the role asks for them */}
                            {needsFiles && (
                                <section className="ms-card">
                                    <div>
                                        <h3>Additional files</h3>
                                        <p className="ms-muted ms-small">This role asks for extra documents, such as a cover letter. Add at least one.</p>
                                    </div>
                                    <div
                                        className={"ms-drop" + (dragging ? " on" : "")}
                                        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                                        onDragLeave={() => setDragging(false)}
                                        onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
                                    >
                                        <span>Drag files here, or</span>
                                        <button className="ms-btn s sm" type="button" onClick={() => fileInput.current && fileInput.current.click()} disabled={submitting}>
                                            Choose files
                                        </button>
                                        <input ref={fileInput} type="file" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}/>
                                    </div>
                                    {files.length > 0 && (
                                        <ul className="ms-files">
                                            {files.map((f) => (
                                                <li key={f.name}>
                                                    <span className="ms-file-name">{f.name}</span>
                                                    <button type="button" className="ms-link" onClick={() => removeFile(f.name)} disabled={submitting}>Remove</button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </section>
                            )}

                            {/* Consent */}
                            <label className={"ms-card ms-check" + (checked ? " on" : "")}>
                                <input type="checkbox" checked={checked} onChange={() => setChecked(!checked)} disabled={submitting}/>
                                <span>
                                    <b>Share my application</b>
                                    <small>I agree to share my profile data, CV and everything entered above with Samaggi Samagom and {companyName}.</small>
                                </span>
                            </label>

                            {sendError && <div className="ms-banner err" role="alert">{sendError}</div>}

                            <div className="ms-send">
                                <button className="ms-btn p full" onClick={submit} disabled={!canSend || submitting}>
                                    {submitting ? "Sending…" : "Send application"}
                                </button>
                                {!submitting && roleLoaded && missing.length > 0 && (
                                    <p className="ms-muted ms-small">To send, {missing.join(" and ")}.</p>
                                )}
                            </div>
                        </>}
                    </div>
                </div>
            </main>

            <CFASFooter/>
        </div>
    );
};

const CSS = `
@import url("https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700;800&family=Figtree:wght@400;500;600;700&display=swap");
.ms{
  --green:#004225;--deep:#002B1B;--sage:#A7B8A5;--gold:#C3A56A;--gold-deep:#8E7340;--gold-soft:#F3EAD6;
  --cream:#F4EFE5;--ink:#10251C;--muted:#5B6B62;--line:#E1E6DD;--soft:#E6EEE6;
  --ok:#2E7D4F;--ok-soft:#DDF0E3;--warn:#A8651A;--warn-soft:#F8EAD6;--err:#B3261E;--err-soft:#FBE9E7;
  --sh:0 1px 2px rgba(0,43,27,.05),0 10px 28px rgba(0,43,27,.08);
  --f-display:"soleil","Soleil","Outfit",system-ui,sans-serif;
  --f-body:"proxima-nova","Proxima Nova","Figtree",system-ui,sans-serif;--r:14px;
  background:var(--cream);color:var(--ink);font-family:var(--f-body);font-size:16px;line-height:1.55;min-height:100vh;
  /* Full-bleed: break out of CFASWrapper's boxed width so the page spans the whole viewport */
  width:100vw;max-width:100vw;margin-left:calc(50% - 50vw);margin-right:calc(50% - 50vw);overflow-x:clip;
  /* Room for the fixed nav; column layout keeps the footer at the bottom */
  padding-top:66px;display:flex;flex-direction:column;
}
html:has(.ms){overflow-x:hidden}
.ms .cfn{position:fixed;top:0;left:0;right:0;z-index:1000}

.ms *{box-sizing:border-box}
.ms h1,.ms h2,.ms h3{font-family:var(--f-display);color:var(--deep);margin:0;line-height:1.15}
.ms h1{font-size:clamp(2rem,4vw,2.6rem);font-weight:800;letter-spacing:-.02em}
.ms h2{font-size:1.15rem;font-weight:700}
.ms h3{font-size:1.02rem;font-weight:700}
.ms p{margin:0}
.ms-muted{color:var(--muted)}.ms-small{font-size:.88rem}
.ms-in{max-width:1120px;margin:0 auto;padding-inline:20px}
.ms-grow{flex:1;min-width:0}
.ms-lbl{display:block;font-size:.72rem;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:var(--muted);margin-bottom:2px}

/* layout */
.ms-main{padding-block:32px 80px;flex:1}
.ms-narrow{max-width:700px}
.ms-stack{display:grid;gap:16px;min-width:0;align-content:start}
.ms-title{display:grid;gap:6px;margin-bottom:6px}
.ms-title p{font-size:1.05rem}
.ms-card{background:#fff;border-radius:var(--r);padding:20px 22px;display:grid;gap:12px;min-width:0;box-shadow:var(--sh)}
.ms-card-head{display:flex;justify-content:space-between;align-items:center;gap:12px}

/* breadcrumb + links */
.ms-crumbs{display:flex;align-items:center;flex-wrap:wrap;gap:6px;font-size:.92rem;color:var(--muted)}
.ms-link{all:unset;cursor:pointer;color:var(--deep);font-weight:600;text-decoration:underline;text-underline-offset:3px}
.ms-link:focus-visible{outline:2px solid var(--gold);outline-offset:2px}
.ms-link:disabled{opacity:.5;cursor:not-allowed}

/* role summary */
.ms-role-head{display:flex;align-items:center;gap:14px}
.ms-logo{width:48px;height:48px;flex:none;border-radius:12px;border:1px solid var(--line);background:#fff;display:grid;place-items:center;overflow:hidden}
.ms-logo img{width:88%;height:88%;object-fit:contain;display:block}
.ms-logo span{font-family:var(--f-display);font-weight:800;font-size:.8rem;color:var(--green)}
.ms-details{border-top:1px solid var(--line);padding-top:10px}
.ms-details summary{cursor:pointer;font-weight:600;color:var(--deep);font-size:.92rem}
.ms-details summary:focus-visible{outline:2px solid var(--gold);outline-offset:2px;border-radius:4px}
.ms-desc{white-space:pre-wrap;color:var(--muted);font-size:.95rem;margin-top:8px}

/* profile */
.ms-dl{display:grid;grid-template-columns:140px 1fr;gap:8px 20px;margin:0;font-size:.98rem}
.ms-dl dt{font-weight:700;color:var(--deep)}
.ms-dl dd{margin:0}
@media (max-width:520px){.ms-dl{grid-template-columns:1fr;gap:2px}.ms-dl dd{margin-bottom:8px}}

/* CV */
.ms-cv{display:grid;gap:4px;border:1.5px dashed var(--line);border-radius:12px;padding:14px 16px;background:#FAFAF7}
.ms-cv b{color:var(--deep)}
.ms-cv.ok{border-style:solid;border-color:#BFE0CB;background:#F3FAF5}
.ms-cv.ok b{color:var(--ok)}
.ms-cv .ms-link{justify-self:start;font-size:.88rem}

/* question */
.ms-q{display:grid;gap:2px}
.ms-card textarea{font:inherit;font-size:.98rem;color:var(--ink);background:#fff;border:1.5px solid var(--line);border-radius:12px;padding:12px 14px;resize:vertical;min-height:120px;width:100%}
.ms-card textarea::placeholder{color:#94A099}
.ms-card textarea:focus{outline:2px solid var(--green);outline-offset:1px;border-color:var(--green)}

/* files */
.ms-drop{display:flex;align-items:center;justify-content:center;flex-wrap:wrap;gap:12px;border:1.5px dashed var(--sage);border-radius:12px;padding:20px;background:#FAFBF9;color:var(--muted);font-size:.92rem}
.ms-drop.on{border-color:var(--green);background:#F2FAF4}
.ms-files{list-style:none;margin:0;padding:0;display:grid;gap:6px}
.ms-files li{display:flex;justify-content:space-between;align-items:center;gap:12px;padding:10px 14px;border-radius:10px;background:var(--cream);font-size:.92rem}
.ms-file-name{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ms-files .ms-link{font-size:.84rem}

/* consent */
.ms-check{display:flex;gap:12px;align-items:flex-start;cursor:pointer;grid-template-columns:none}
.ms-check input{width:20px;height:20px;margin:2px 0 0;accent-color:var(--deep);flex:none;cursor:pointer}
.ms-check b{display:block;color:var(--deep);font-size:.98rem}
.ms-check small{display:block;color:var(--muted);font-size:.86rem;margin-top:2px}
.ms-check.on{box-shadow:0 0 0 2px var(--deep),var(--sh)}

/* send */
.ms-send{display:grid;gap:8px;justify-items:center;margin-top:4px}
.ms-banner{padding:12px 14px;border-radius:12px;font-size:.92rem}
.ms-banner.err{background:var(--err-soft);color:var(--err)}

/* buttons */
.ms-btn{all:unset;cursor:pointer;box-sizing:border-box;display:inline-flex;align-items:center;justify-content:center;gap:8px;font-family:var(--f-display);font-weight:700;font-size:.98rem;min-height:48px;padding:10px 22px;border-radius:12px;border:1.5px solid transparent;white-space:nowrap;transition:transform .12s}
.ms-btn:hover{transform:translateY(-1px)}
.ms-btn:focus-visible{outline:2px solid var(--gold);outline-offset:2px}
.ms-btn.p{background:var(--deep);color:#fff;box-shadow:0 6px 18px rgba(0,43,27,.28)}
.ms-btn.s{background:#fff;color:var(--deep);border-color:var(--deep)}
.ms-btn.sm{min-height:38px;padding:6px 14px;font-size:.86rem;border-radius:9px}
.ms-btn.full{width:100%}
.ms-btn:disabled{background:#DCE3DC;color:#7E8C84;box-shadow:none;cursor:not-allowed;transform:none;border-color:transparent}

.ms-empty{text-align:center;justify-items:center;padding:32px 24px}
@media (prefers-reduced-motion:reduce){.ms-btn{transition:none}}
`;

export default ApplySubmit;